
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useCallback, useRef } from 'react';
import ResponsiveTable from './ResponsiveTable';
import styles from './Table.module.scss';
import TableHead from './TableHead';
import TableToolbar from './TableToolbar';

const rowsPerPage = 20;

function Table({ ref, height, modelName, params, info, filtersInfo, headCells, hideToolbar, multiple, renderRow, renderMobileCard, onCreate, showSelect, responsive = true, onClickView, onClickEdit, onClickDelete, onClickUnsubscribe }) {
  // Original table implementation for non-responsive mode
  const tableToolbarRef = useRef();
  const tablePaginationRef = useRef();
  const dataRows = get(info, 'data.items', []);
  const { current_page, items_per_page, total_item_count } = get(info, 'data', {});

  const handleRequestSort = useCallback((event, property) => {
    const isAsc = params.getSort() === property && params.getOrder() === 'asc';
    params.setSort(property);
    params.setOrder(isAsc ? 'desc' : 'asc');
  }, [params]);

  const handleSelectAllClick = useCallback((event) => {
    if (event.target.checked) {
      const selected = params.getSelected();
      let nextSelected = selected.union(dataRows.map((n) => n.id));
      if (nextSelected.size === selected.size) {
        nextSelected = selected.filter(id => !dataRows.map(n => n.id).includes(id));
      }
      params.setSelected(nextSelected);
    } else {
      let selected = params.getSelected();
      selected = selected.clear();
      params.setSelected(selected);
    }
  }, [params, dataRows]);

  const handleClick = useCallback((event, id) => {
    const selected = params.getSelected();
    if (selected.has(id)) {
      params.setSelected(selected.remove(id));
    } else {
      params.setSelected(multiple ? selected.add(id) : selected.clear().add(id));
    }
  }, [params, multiple]);

  const handleChangePage = useCallback((event, newPage) => {
    params.setPage(newPage + 1);
  }, [params]);

  const handleChangeFilter = useCallback((name, value) => {
    params.setFilters(params.getFilters().set(name, value));
  }, [params]);

  // Use ResponsiveTable if responsive mode is enabled
  if (responsive) {
    return (
      <ResponsiveTable
        ref={ref}
        height={height}
        modelName={modelName}
        params={params}
        info={info}
        filtersInfo={filtersInfo}
        headCells={headCells}
        hideToolbar={hideToolbar}
        multiple={multiple}
        renderRow={renderRow}
        renderMobileCard={renderMobileCard}
        onCreate={onCreate}
        showSelect={showSelect}
        onClickView={onClickView}
        onClickEdit={onClickEdit}
        onClickDelete={onClickDelete}
        onClickUnsubscribe={onClickUnsubscribe}
      />
    );
  }

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%' }}>
        {
          !hideToolbar && (
            <>
              <div className={styles.topSpace} />
              <TableToolbar
                ref={tableToolbarRef}
                modelName={modelName}
                numSelected={params.getSelected().size}
                filters={params.getFilters()}
                filtersInfo={filtersInfo}
                onCreate={onCreate}
                onChangeFilter={handleChangeFilter}
              />
            </>
          )
        }

        <TableContainer
          sx={height ? { maxHeight: height - (tableToolbarRef.current?.clientHeight || 0) - (tablePaginationRef.current?.clientHeight || 0) } : {}}
        >
          <MuiTable stickyHeader>
            <TableHead
              headCells={headCells}
              numSelected={params.getSelected().size}
              order={params.getOrder()}
              orderBy={params.getSort()}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={total_item_count}
              showSelect={showSelect}
            />
            <TableBody>
              {dataRows.map((row, index) => {
                const isItemSelected = params.getSelected().includes(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return renderRow({ isItemSelected, labelId, row, handleClick }, index);
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>

        <TablePagination
          ref={tablePaginationRef}
          rowsPerPageOptions={[items_per_page]}
          component="div"
          count={total_item_count}
          rowsPerPage={rowsPerPage}
          page={current_page - 1}
          onPageChange={handleChangePage}
          className={styles.pagination}
          classes={{
            root: styles.root,
            select: styles.select,
            spacer: styles.spacer,
            toolbar: styles.pagiToolbar,
          }}
          showFirstButton
          showLastButton
        />
      </Paper>
    </Box>
  );
}

Table.propTypes = {
  ref: PropTypes.object,
  height: PropTypes.number,
  modelName: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  info: PropTypes.object.isRequired,
  filtersInfo: PropTypes.object,
  headCells: PropTypes.array.isRequired,
  hideToolbar: PropTypes.bool,
  multiple: PropTypes.bool,
  renderRow: PropTypes.func.isRequired,
  renderMobileCard: PropTypes.func,
  onCreate: PropTypes.func,
  showSelect: PropTypes.bool,
  responsive: PropTypes.bool,
  onClickView: PropTypes.func,
  onClickEdit: PropTypes.func,
  onClickDelete: PropTypes.func,
  onClickUnsubscribe: PropTypes.func,
};

export default Table;
